'use client';

import './account-settings-sections.scss';

import type { PropsWithChildren } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@sola/ark-core/molecules/tooltip/tooltip.tsx';
import { SupporterIdentity } from '@sola/ark-skippy/molecules/supporter-identity/supporter-identity.tsx';
import type { GraphQLSupporterAddress } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { SENSITIVE_MASK_CHAR } from '@sola/shared-utils/constants.ts';
import type { SupporterDetailsData } from '@sola/supporter-sdk/queries.ts';
import {
  getSupporterFullName,
  getSupporterInitials,
} from '@sola/supporter-sdk/utils/name-format.ts';

import { EditContactInfoModal } from '../../organisms/edit-contact-info-modal/edit-contact-info-modal.tsx';
import { EditMailingModal } from '../../organisms/edit-mailing-modal/edit-mailing-modal.tsx';
import { LocalAlertsProvider } from '../local-alerts/local-alerts-provider.tsx';

type AccountSettingsSectionProps = {
  title: string;
  children: React.ReactNode;
};

const EditButton = ({
  onClick,
  tooltip,
  disable,
}: {
  onClick?: () => void;
  tooltip?: string;
  disable?: boolean;
}) => {
  const { t } = useTranslation('core');
  const label = t('actions.edit');
  return tooltip ? (
    <Tooltip>
      <TooltipContent
        sideOffset={-5}
        usePortal
        align="end"
        side="top"
        size="sm"
      >
        <p>{tooltip}</p>
      </TooltipContent>
      <TooltipTrigger>
        <LinkStyle iconBefore="lock-01" size="lg" noUnderline>
          <button type="button" onClick={onClick} disabled={disable}>
            {label}
          </button>
        </LinkStyle>
      </TooltipTrigger>
    </Tooltip>
  ) : (
    <LinkStyle noUnderline size="lg">
      <button type="button" onClick={onClick} disabled={disable}>
        {label}
      </button>
    </LinkStyle>
  );
};

const ItemBlock = ({
  label,
  value,
}: {
  label?: string;
  value?: React.ReactNode;
}) => {
  return (
    <div className="flex flex-col flex-1 gap-snug-xl">
      {label && (
        <span className="body-sm label-neutral-mild text-wrap-nowrap">
          {label}
        </span>
      )}
      <span className="body-sm label-neutral-amplified">{value}</span>
    </div>
  );
};

const ProfileBlock = ({ initials }: { initials?: string }) => {
  const { t } = useTranslation('my-settings');

  return (
    <div className="flex flex-1 pr-comfy-lg gap-comfy-sm items-center">
      <div className="Settings__profile rounded-full flex rounded-full shrink-0 items-center justify-center">
        <SupporterIdentity variant="minimal" initials={initials} />
      </div>
      <span className="label-md label-neutral-mild">
        {t('my-settings:account.profile.description')}
      </span>
    </div>
  );
};

export const AccountSettingsSection: React.FC<AccountSettingsSectionProps> = ({
  title,
  children,
}) => {
  return (
    <div className="Settings flex flex-col gap-comfy-xl max-w-full py-comfy-lg md:py-comfy-xl">
      {title && (
        <h3 className="label-neutral-amplified label-potent label-xs">
          {title}
        </h3>
      )}
      <div className="flex gap-snug-xl">{children}</div>
    </div>
  );
};

export const AccountSettingsContent: React.FC<PropsWithChildren> = ({
  children,
}) => (
  <div className="flex flex-1 flex-col gap-comfy-lg md:flex-row">
    {children}
  </div>
);

export const SettingDivider = () => <div className="Settings__divider" />;

export const AccountInformation = ({
  supporter,
}: {
  supporter: SupporterDetailsData;
}) => {
  const { t } = useTranslation('my-settings');
  return (
    <AccountSettingsSection title={t('my-settings:account.info.title')}>
      <AccountSettingsContent>
        <ItemBlock
          label={t('my-settings:account.info.nameLabel')}
          value={getSupporterFullName(supporter)}
        />
        <ItemBlock
          label={t('my-settings:account.info.sponsorNumberLabel')}
          value={supporter.globalId}
        />
      </AccountSettingsContent>
      <EditButton disable tooltip={t('my-settings:account.editTooltip')} />
    </AccountSettingsSection>
  );
};

export const AccountProfile = ({
  supporter,
}: {
  supporter: SupporterDetailsData;
}) => {
  const { t } = useTranslation('my-settings');
  const initials = getSupporterInitials(supporter);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AccountSettingsSection title={t('my-settings:account.profile.title')}>
      <AccountSettingsContent>
        <ItemBlock
          label={t('my-settings:account.profile.preferredNameLabel')}
          value={supporter.preferredName}
        />
        <ProfileBlock initials={initials} />
      </AccountSettingsContent>
      <EditButton onClick={() => setIsOpen(true)} />
      <EditContactInfoModal
        supporter={supporter}
        open={isOpen}
        onOpenChange={setIsOpen}
        onClose={() => setIsOpen(false)}
      />
    </AccountSettingsSection>
  );
};

export const AccountUsernamePassword = ({ username }: { username: string }) => {
  const { t } = useTranslation('my-settings');

  return (
    <AccountSettingsSection title={t('my-settings:account.credentials.title')}>
      <AccountSettingsContent>
        <ItemBlock
          label={t('my-settings:account.credentials.usernameLabel')}
          value={username}
        />
        <ItemBlock
          label={t('my-settings:account.credentials.passwordLabel')}
          value={SENSITIVE_MASK_CHAR.repeat(12)}
        />
      </AccountSettingsContent>
      <EditButton onClick={() => console.log('click')} />
    </AccountSettingsSection>
  );
};

export const AccountMailingAddress = ({
  addressDetails,
}: {
  addressDetails: GraphQLSupporterAddress;
}) => {
  const { t } = useTranslation('my-settings');
  const [isOpen, setIsOpen] = useState(false);

  return (
    <LocalAlertsProvider>
      <AccountSettingsSection title={t('my-settings:account.address.title')}>
        <AccountSettingsContent>
          <ItemBlock
            value={
              <div className="flex flex-col gap-snug-lg">
                <span>{addressDetails?.street}</span>
                <span>
                  {addressDetails?.city}, {addressDetails?.state}{' '}
                  {addressDetails?.postalCode}
                </span>
                <span>{addressDetails?.country}</span>
              </div>
            }
          />
        </AccountSettingsContent>
        <EditButton onClick={() => setIsOpen(true)} />
        <EditMailingModal
          addressDetails={addressDetails}
          open={isOpen}
          onOpenChange={setIsOpen}
          onClose={() => setIsOpen(false)}
        />
      </AccountSettingsSection>
    </LocalAlertsProvider>
  );
};

export const AccountEmailPhone = ({
  supporter,
}: {
  supporter: SupporterDetailsData;
}) => {
  const { t } = useTranslation('my-settings');
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AccountSettingsSection title={t('my-settings:account.contact.title')}>
      <AccountSettingsContent>
        <ItemBlock
          label={t('my-settings:account.contact.emailLabel')}
          value={supporter.email}
        />
        <ItemBlock
          label={t('my-settings:account.contact.phoneLabel')}
          value={supporter.mobilePhone}
        />
      </AccountSettingsContent>
      <EditButton onClick={() => setIsOpen(true)} />
      <EditContactInfoModal
        supporter={supporter}
        open={isOpen}
        onOpenChange={setIsOpen}
        onClose={() => setIsOpen(false)}
      />
    </AccountSettingsSection>
  );
};
